import React, { useMemo, useState } from 'react';
import { toAbsoluteUrl } from '../../../../../../../_metronic/_helpers/AssetsHelpers';
import InviteModal from 'app/pages/Gamification/components/InviteModal';
import { MGM_CHALLENGE_STUB } from 'app/components/Mgm/mgmChallengeStub';
import { useGamificationQuery } from 'app/pages/Gamification/hooks/useGamificationQuery';
import { getGamificationLevelPresentation } from 'app/helpers/gamification/getGamificationLevelPresentation';
import GamificationMobileMenuIcon from '../GamificationMenuIcon/GamificationMobileMenuIcon';
import MenuDrawerItem from '../MenuDrawerItem';
import { getFlattenedMenuItems } from './getFlattenedMenuItems';
import type { FlattenedMenuEntry } from '../../types';
import { useResolvedMenu } from '../../hooks';
import { isCommunityLink, openCommunity } from 'app/services/community';
import { MGM_INVITE_ACTION_KEY } from '../../types';
import {
  trackMenuItemSelected,
  trackMenuProfileMgmClicked,
} from '../../trackMenuEvents';
import { NavGroupTitle } from './styles';
import * as S from './styles';

interface MobileMenuDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (path: string) => void;
}

function MobileMenuDrawer({
  isOpen,
  onClose,
  onNavigate,
}: MobileMenuDrawerProps) {
  const {
    mainItems,
    settingsItems,
    drawerConfig,
    gamificationMenuItem,
    isMgmEnabled,
  } = useResolvedMenu();
  const { userStats, levels, loading: gamificationLoading } =
    useGamificationQuery();
  const gamificationPresentation = useMemo(
    () => getGamificationLevelPresentation(userStats, levels),
    [userStats, levels],
  );
  const gamificationMobileLabel = useMemo(() => {
    if (gamificationLoading) {
      return '';
    }
    return `Nível ${gamificationPresentation.currentLevelIndex + 1}: ${gamificationPresentation.currentLevelConfig.name}`;
  }, [gamificationLoading, gamificationPresentation]);
  const gamificationMobileIcon = useMemo(
    () => (
      <GamificationMobileMenuIcon
        imageSrc={gamificationPresentation.imageSrc}
        imageAlt={gamificationPresentation.imageAlt}
        loading={gamificationLoading}
      />
    ),
    [
      gamificationPresentation.imageSrc,
      gamificationPresentation.imageAlt,
      gamificationLoading,
    ],
  );
  const items = useMemo(
    () =>
      getFlattenedMenuItems({
        mainItems,
        settingsItems,
        drawerConfig,
        gamificationMenuItem,
        gamificationMobileLabel,
        gamificationMobileIcon,
        showMgmInviteFooterAction: isMgmEnabled,
      }),
    [
      mainItems,
      settingsItems,
      drawerConfig,
      gamificationMenuItem,
      gamificationMobileLabel,
      gamificationMobileIcon,
      isMgmEnabled,
    ],
  );
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  const handleItemClick = (entry: Extract<FlattenedMenuEntry, { type: 'item' }>) => {
    if (entry.disabled) return;

    if (entry.actionKey === MGM_INVITE_ACTION_KEY) {
      trackMenuProfileMgmClicked();
      setIsInviteModalOpen(true);
      onClose();
      return;
    }

    trackMenuItemSelected({
      groupId: entry.groupId,
      itemName: entry.label,
      categoryParent: entry.categoryParent ?? null,
    });
    if (isCommunityLink(entry.path)) {
      openCommunity();
    } else if (entry.externalLink) {
      window.open(entry.path, '_blank', 'noopener,noreferrer');
    } else if (entry.path) {
      onNavigate(entry.path);
    }
    onClose();
  };

  const renderEntry = (entry: FlattenedMenuEntry) => {
    if (entry.type === 'sectionTitle') {
      return <NavGroupTitle key={entry.id}>{entry.label}</NavGroupTitle>;
    }
    if (entry.type === 'divider') {
      return <S.Divider key={entry.id} />;
    }
    if (entry.actionKey === MGM_INVITE_ACTION_KEY) {
      return (
        <MenuDrawerItem
          key={entry.id}
          icon={entry.icon}
          label={entry.label}
          isCollapsed={false}
          showIcon={false}
          onClick={() => handleItemClick(entry)}
        />
      );
    }

    const icon =
      entry.iconSize !== undefined && React.isValidElement(entry.icon)
        ? React.cloneElement(
            entry.icon as React.ReactElement<{ size?: number; color?: string }>,
            {
              size: entry.iconSize,
              ...(entry.iconColor && { color: entry.iconColor }),
            },
          )
        : entry.icon;

    return (
      <MenuDrawerItem
        key={entry.id}
        icon={icon}
        label={entry.label}
        isCollapsed={false}
        route={entry.externalLink ? undefined : entry.path}
        onClick={() => handleItemClick(entry)}
        iconPosition={entry.iconPosition}
        badge={entry.badge}
        externalLink={entry.externalLink}
        showIcon={entry.showIcon ?? !entry.externalLink}
        disabled={entry.disabled}
      />
    );
  };

  return (
    <>
      {isOpen && (
        <>
          <S.Overlay onClick={onClose} aria-hidden />
          <S.Panel $isOpen={isOpen} onClick={(e) => e.stopPropagation()}>
            <S.Logo src={toAbsoluteUrl('/media/logos/logo.svg')} alt="Jusfy" />
            <S.ContentWrapper>{items.map(renderEntry)}</S.ContentWrapper>
          </S.Panel>
        </>
      )}
      <InviteModal
        isOpen={isInviteModalOpen}
        challenge={MGM_CHALLENGE_STUB}
        onClose={() => setIsInviteModalOpen(false)}
      />
    </>
  );
}

export default MobileMenuDrawer;
